function getFocusableElements(container) {
	return Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
		)
	);
}

class HTMLUpdateUtility {
	/**
	 * Used to swap an HTML node with a new node.
	 * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
	 *
	 * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
	 */
	static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
		preProcessCallbacks?.forEach((callback) => callback(newContent));

		const newNodeWrapper = document.createElement('div');
		HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
		const newNode = newNodeWrapper.firstChild;

		// dedupe IDs
		const uniqueKey = Date.now();
		oldNode.querySelectorAll('[id], [form]').forEach((element) => {
			element.id && (element.id = `${element.id}-${uniqueKey}`);
			element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
		});

		oldNode.parentNode.insertBefore(newNode, oldNode);
		oldNode.style.display = 'none';

		postProcessCallbacks?.forEach((callback) => callback(newNode));

		setTimeout(() => oldNode.remove(), 500);
	}

	// Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
	static setInnerHTML(element, html) {
		element.innerHTML = html;
		element.querySelectorAll('script').forEach((oldScriptTag) => {
			const newScriptTag = document.createElement('script');
			Array.from(oldScriptTag.attributes).forEach((attribute) => {
				newScriptTag.setAttribute(attribute.name, attribute.value);
			});
			newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
			oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
		});
	}
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
	var elements = getFocusableElements(container);
	var first = elements[0];
	var last = elements[elements.length - 1];

	removeTrapFocus();

	trapFocusHandlers.focusin = (event) => {
		if (event.target !== container && event.target !== last && event.target !== first) return;

		document.addEventListener('keydown', trapFocusHandlers.keydown);
	};

	trapFocusHandlers.focusout = function () {
		document.removeEventListener('keydown', trapFocusHandlers.keydown);
	};

	trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
		// On the last focusable element and tab forward, focus the first element.
		if (event.target === last && !event.shiftKey) {
			event.preventDefault();
			first.focus();
		}

		//  On the first focusable element and tab backward, focus the last element.
		if ((event.target === container || event.target === first) && event.shiftKey) {
			event.preventDefault();
			last.focus();
		}
	};

	document.addEventListener('focusout', trapFocusHandlers.focusout);
	document.addEventListener('focusin', trapFocusHandlers.focusin);

	if (elementToFocus) {
		elementToFocus.focus();
		if (
			elementToFocus.tagName === 'INPUT' &&
			['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
			elementToFocus.value
		) {
			elementToFocus.setSelectionRange(0, elementToFocus.value.length);
		}
	}
}

function removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', trapFocusHandlers.focusin);
	document.removeEventListener('focusout', trapFocusHandlers.focusout);
	document.removeEventListener('keydown', trapFocusHandlers.keydown);

	if (elementToFocus) elementToFocus.focus();
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
	document.querySelector(':focus-visible');
} catch (e) {
	focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
	const navKeys = [
		'ARROWUP',
		'ARROWDOWN',
		'ARROWLEFT',
		'ARROWRIGHT',
		'TAB',
		'ENTER',
		'SPACE',
		'ESCAPE',
		'HOME',
		'END',
		'PAGEUP',
		'PAGEDOWN',
	];
	let currentFocusedElement = null;
	let mouseClick = null;

	window.addEventListener('keydown', (event) => {
		if (navKeys.includes(event.code.toUpperCase())) {
			mouseClick = false;
		}
	});

	window.addEventListener('mousedown', (event) => {
		mouseClick = true;
	});

	window.addEventListener(
		'focus',
		() => {
			if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

			if (mouseClick) return;

			currentFocusedElement = document.activeElement;
			currentFocusedElement.classList.add('focused');
		},
		true
	);
}

class VariantSelects extends HTMLElement {
	constructor() {
		super();
	}

	connectedCallback() {
		this.addEventListener('change', (event) => {
			const target = this.getInputForEventTarget(event.target);
			this.updateSelectionMetadata(event);

			publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
				data: {
					event,
					target,
					selectedOptionValues: this.selectedOptionValues,
				},
			});
		});
	}

	updateSelectionMetadata({ target }) {
		const { value, tagName } = target;

		if (tagName === 'SELECT' && target.selectedOptions.length) {
			Array.from(target.options)
				.find((option) => option.getAttribute('selected'))
				.removeAttribute('selected');
			target.selectedOptions[0].setAttribute('selected', 'selected');

			const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
			const selectedDropdownSwatchValue = target
				.closest('.product-form__input')
				.querySelector('[data-selected-value] > .swatch');
			if (!selectedDropdownSwatchValue) return;
			if (swatchValue) {
				selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
				selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
			} else {
				selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
				selectedDropdownSwatchValue.classList.add('swatch--unavailable');
			}

			selectedDropdownSwatchValue.style.setProperty(
				'--swatch-focal-point',
				target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
			);
		} else if (tagName === 'INPUT' && target.type === 'radio') {
			const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
			if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
		}
	}

	getInputForEventTarget(target) {
		return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
	}

	get selectedOptionValues() {
		return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
			({ dataset }) => dataset.optionValueId
		);
	}
}

customElements.define('variant-selects', VariantSelects);

class CollapseDrawer extends HTMLElement {
	constructor() {
		super();
		//remove all classes from this that start with 'peer-checked'
		this.className = this.className.replace(/\bpeer-checked\S+/g, '');

		const inner = document.createElement('div');
		while (this.firstChild) inner.appendChild(this.firstChild);
		//remove all the classes from this, and apply them to inner
		inner.className = this.className;
		inner.classList.remove('hidden');

		this.className = '';
		if (this.dataset.responsive) {
			//lg:!h-auto lg:!block
			this.classList.add(`${this.dataset.responsive}:block`, `${this.dataset.responsive}:!h-auto`);
		}
		this.appendChild(inner);
		this.content = inner;
		this.classList.add('transition-height', 'overflow-hidden', 'hidden');

		this.checkbox = document.getElementById(this.dataset.toggle);
		this.toggles = document.querySelectorAll(`label[for="${this.dataset.toggle}"]`);

		//detect when the checkbox is toggled
		this.checkbox.addEventListener('change', (event) => {
			if (this.isOpen()) this.open();
			else this.close();
		});

		if (this.isOpen()) this.open();
	}

	isOpen() {
		return this.checkbox.checked;
	}

	open(triggerElement = null) {
		this.checkbox.checked = true;
		const onTransitionEnd = () => {
			this.removeEventListener('transitionend', onTransitionEnd);
			this.style.removeProperty('height');
			this.classList.remove('h-0');
			this.classList.add('h-auto');
		};
		this.addEventListener('transitionend', onTransitionEnd);
		this.classList.add('h-0', 'block');
		this.classList.remove('hidden');
		//get the height of inner contents
		const innerHeight = this.content.offsetHeight;
		this.style.setProperty('height', `${innerHeight}px`);
	}

	close(focusToggle = true) {
		const onTransitionEnd = () => {
			this.removeEventListener('transitionend', onTransitionEnd);
			this.classList.remove('h-0', 'block');
			this.classList.add('hidden');
		};
		this.checkbox.checked = false;
		this.style.setProperty('height', `${this.offsetHeight}px`);
		this.classList.remove('h-auto');
		this.addEventListener('transitionend', onTransitionEnd);
		this.classList.add('h-0');
		//force reflow
		this.offsetHeight;
		this.style.removeProperty('height');
	}
}

customElements.define('collapse-drawer', CollapseDrawer);

class OffcanvasModal extends HTMLElement {
	constructor() {
		super();

		//move this to the end of the body
		if (this.dataset.appendToBody) {
			document.body.appendChild(this);
		}

		//remove all classes from this that start with 'peer-checked'
		this.className = this.className.replace(/\bpeer-checked\S+/g, '');
		this.checkbox = document.getElementById(this.dataset.toggle);
		this.toggles = document.querySelectorAll(`label[for="${this.dataset.toggle}"]`);
		this.toggleLinks = document.querySelectorAll(`a[data-target="${this.id}"], button[data-target="${this.id}"]`);
		this.closeLinks = document.querySelectorAll(`button[data-close="${this.id}"]`);
		this.backdrop = this.querySelector("[data-role='backdrop']");
		this.content = this.querySelector("[data-role='content']");

		if (this.dataset.align == 'right') {
			this.transitionContentClass = 'translate-x-full';
		} else if (this.dataset.align == 'top') {
			this.transitionContentClass = '-translate-y-full';
		} else if (this.dataset.align == 'center') {
			this.transitionContentClass = '-translate-y-[50vh]';
		} else {
			this.transitionContentClass = '-translate-x-full';
		}

		if (this.backdrop) {
			this.backdrop.classList.add('transition-opacity');
		}

		if (this.content) {
			this.content.classList.add('transition-transform');
		}

		this.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());

		//detect when the checkbox is toggled
		this.checkbox.addEventListener('change', (event) => {
			// Only handle if not already processing (prevents double calls from toggleLinks)
			if (!this._processing) {
				if (this.isOpen()) this.open(event.currentTarget);
				else this.close();
			}
		});

		this.toggleLinks.forEach((link) => {
			link.addEventListener('click', (event) => {
				event.preventDefault();
				this._processing = true;
				this.checkbox.checked = !this.checkbox.checked;
				if (this.isOpen()) this.open(event.currentTarget);
				else this.close();
				// Reset processing flag after a brief delay to allow change event to be ignored
				setTimeout(() => {
					this._processing = false;
				}, 0);
			});
		});

		this.closeLinks.forEach((link) => {
			link.addEventListener('click', (event) => {
				event.preventDefault();
				this.checkbox.checked = false;
				this.close();
			});
		});
	}

	isOpen() {
		return this.checkbox.checked;
	}

	open(triggerElement = null) {
		//find open modals and close them
		const openModals = document.querySelectorAll(`offcanvas-modal:not([id="${this.id}"])`);

		openModals.forEach((modal) => {
			if (modal.isOpen()) {
				modal.close();
			}
		});

		this.classList.add('hidden');
		//force a reflow
		this.offsetWidth;

		this.checkbox.checked = true;
		document.body.classList.add('overflow-hidden', 'has-overlay');
		this.backdrop.classList.add('opacity-0');
		this.content.classList.add(this.transitionContentClass);

		this.classList.remove('hidden');
		//force a reflow
		this.offsetWidth;
		trapFocus(this.querySelector('[tabindex="-1"]'), this.querySelector('input:not([type="hidden"])'));
		this.content.classList.remove(this.transitionContentClass);
		this.backdrop.classList.remove('opacity-0');
	}

	close(focusToggle = true, after = null) {
		const onTransitionEnd = () => {
			//remove this event listener
			this.backdrop.removeEventListener('transitionend', onTransitionEnd);

			this.backdrop.classList.remove('opacity-0');
			this.content.classList.remove(this.transitionContentClass);
			this.classList.add('hidden');
			this.classList.remove('block');
			document.body.classList.remove('overflow-hidden', 'has-overlay');

			if (typeof after === 'function') {
				after();
			}
		};

		this.classList.remove('hidden');
		//force a reflow
		this.offsetWidth;

		removeTrapFocus(focusToggle ? this : null);
		this.checkbox.checked = false;

		this.backdrop.addEventListener('transitionend', onTransitionEnd);

		this.backdrop.classList.add('opacity-0');
		this.content.classList.add(this.transitionContentClass);
	}
}

customElements.define('offcanvas-modal', OffcanvasModal);

document.addEventListener('closeModal', (event) => {
	if (typeof event.detail === 'object') {
		const { element, after } = event.detail;
		if (element === 'all') {
			const modals = document.querySelectorAll('offcanvas-modal');
			let closeCount = 0;
			modals.forEach((modal) => {
				if (modal.isOpen()) {
					modal.close(true, after || null);
					closeCount++;
				}
			});
		}
		const modal = document.getElementById(element);
		if (modal) {
			modal.close();
		}
	} else {
		if (event.detail === 'all') {
			const modals = document.querySelectorAll('offcanvas-modal');
			let closeCount = 0;
			modals.forEach((modal) => {
				if (modal.isOpen()) {
					modal.close(true, event.after || null);
					closeCount++;
				}
			});

			if (closeCount === 0 && event.after) {
				event.after();
			}
			return;
		}
		const modal = document.getElementById(event.detail);
		if (modal) {
			modal.close();
		}
	}
});

// Chat functionality
function openChat() {
	const chatElement = document?.getElementById(`ShopifyChat`)?.shadowRoot?.querySelector(`.chat-app>button`);
	if (chatElement) {
		//trigger a custom event to close all modals
		document.dispatchEvent(
			new CustomEvent('closeModal', {
				detail: {
					element: 'all',
					after: () => {
						chatElement.click();
					},
				},
			})
		);
	} else {
		console.warn('Shopify chat element not found');
	}
}

// Event listeners for chat triggers
document.addEventListener('DOMContentLoaded', function () {
	// Handle links with href="#chat"
	document.addEventListener('click', function (event) {
		if (
			event.target.matches('a[href="#chat"]') ||
			event.target.matches('button[data-target="chat"]') ||
			event.target.closest('a[href="#chat"]') ||
			event.target.closest('[data-target="chat"]')
		) {
			event.preventDefault();
			openChat();
		}
	});

	// Handle buttons with data-target="chat"
	document.addEventListener('click', function (event) {
		if (event.target.matches('button[data-target="chat"]')) {
			event.preventDefault();
			openChat();
		}
	});
});

function debounce(fn, wait) {
	let t;
	return (...args) => {
		clearTimeout(t);
		t = setTimeout(() => fn.apply(this, args), wait);
	};
}

function throttle(fn, delay) {
	let lastCall = 0;
	return function (...args) {
		const now = new Date().getTime();
		if (now - lastCall < delay) {
			return;
		}
		lastCall = now;
		return fn(...args);
	};
}

function fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	};
}

class QuantityInput extends HTMLElement {
	constructor() {
		super();
		this.input = this.querySelector('input');
		this.changeEvent = new Event('change', { bubbles: true });

		this.input.addEventListener('change', this.onInputChange.bind(this));

		this.querySelectorAll('button').forEach((button) =>
			button.addEventListener('click', this.onButtonClick.bind(this))
		);
	}

	quantityUpdateUnsubscriber = undefined;

	connectedCallback() {
		this.validateQtyRules();
		this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
	}

	disconnectedCallback() {
		if (this.quantityUpdateUnsubscriber) {
			this.quantityUpdateUnsubscriber();
		}
	}

	roundToInnerPack(value, innerPack, min = null) {
		if (!innerPack || innerPack <= 0) return value;
		const remainder = value % innerPack;
		if (remainder === 0) return value;
		// Round to nearest multiple of inner pack
		let rounded = Math.round(value / innerPack) * innerPack;
		// Ensure it's at least the minimum value
		if (min !== null && rounded < min) {
			rounded = min;
		}
		return rounded;
	}

	onInputChange(event) {
		const innerPack = this.input.dataset.innerPack ? parseInt(this.input.dataset.innerPack) : null;
		if (innerPack && innerPack > 0) {
			const currentValue = parseInt(this.input.value) || 0;
			const min = this.input.min ? parseInt(this.input.min) : null;
			const roundedValue = this.roundToInnerPack(currentValue, innerPack, min);
			if (roundedValue !== currentValue) {
				this.input.value = roundedValue;
			}
		}
		this.validateQtyRules();
	}

	onButtonClick(event) {
		event.preventDefault();
		const previousValue = parseInt(this.input.value) || 0;
		const innerPack = this.input.dataset.innerPack ? parseInt(this.input.dataset.innerPack) : null;
		const min = this.input.min ? parseInt(this.input.min) : null;

		const button = event.target.tagName.toLowerCase() === 'button' ? event.target : event.target.closest('button');

		button.name === 'plus' ? this.input.stepUp() : this.input.stepDown();

		const newValue = parseInt(this.input.value) || 0;
		
		// Round to inner pack multiple if inner pack exists
		if (innerPack && innerPack > 0 && newValue !== previousValue) {
			const roundedValue = this.roundToInnerPack(newValue, innerPack, min);
			this.input.value = roundedValue;
		}

		if (previousValue !== parseInt(this.input.value)) {
			this.input.dispatchEvent(this.changeEvent);
		}
	}

	validateQtyRules() {
		const value = parseInt(this.input.value);
		if (this.input.min) {
			const min = parseInt(this.input.min);
			const buttonMinus = this.querySelector(".quantity__button[name='minus']");
			buttonMinus.disabled = value <= min;
		}
		if (this.input.max) {
			const max = parseInt(this.input.max);
			const buttonPlus = this.querySelector(".quantity__button[name='plus']");
			buttonPlus.disabled = value >= max;
		}
	}
}

customElements.define('quantity-input', QuantityInput);

class PopupModal extends HTMLElement {
	constructor() {
		super();

		//move this to the end of the body
		if (this.dataset.appendToBody) {
			document.body.appendChild(this);
		}

		//remove all classes from this that start with 'peer-checked'
		this.className = this.className.replace(/\bpeer-checked\S+/g, '');
		this.checkbox = document.getElementById(this.dataset.toggle);
		this.toggles = document.querySelectorAll(`label[for="${this.dataset.toggle}"]`);
		this.toggleLinks = document.querySelectorAll(
			`a[data-toggle="${this.dataset.toggle}"], button[data-toggle="${this.dataset.toggle}"]`
		);
		this.backdrop = this.querySelector("[data-role='backdrop']");
		this.content = this.querySelector("[data-role='content']");
		this.flexMode = this.dataset.flex === 'true';

		if (this.dataset.align == 'right') {
			this.transitionContentClass = 'translate-x-full';
		} else if (this.dataset.align == 'top') {
			this.transitionContentClass = '-translate-y-full';
		} else if (this.dataset.align == 'center') {
			this.transitionContentClass = '-translate-y-[50vh]';
		} else {
			this.transitionContentClass = '-translate-x-full';
		}

		if (this.flexMode) {
			this.classList.add('flex', 'items-center', 'justify-center');
			this.originalFlexClasses = 'flex items-center justify-center';
		}

		if (this.backdrop) {
			this.backdrop.classList.add('transition-opacity');
		}
		if (this.content) {
			this.content.classList.add('transition-transform');
		}

		this.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());

		// Add resize listener for flex mode modals
		if (this.flexMode) {
			this.resizeHandler = () => {
				if (this.isOpen()) {
					this.checkAndAdjustOverflow();
				}
			};
			window.addEventListener('resize', this.resizeHandler);
		}

		if (!this.checkbox) {
			console.error('No checkbox found for popup', this);
			return;
		}

		//detect when the checkbox is toggled
		this.checkbox.addEventListener('change', (event) => {
			if (this.isOpen()) this.open(event.currentTarget);
			else this.close();
		});

		this.toggleLinks.forEach((link) => {
			link.addEventListener('click', (event) => {
				event.preventDefault();
				if (this.isOpen()) {
					this.checkbox.checked = false;
					this.close();
				} else {
					this.checkbox.checked = true;
					this.open(event.currentTarget);
				}
			});
		});
	}

	isOpen() {
		return this.checkbox.checked;
	}

	open(event) {
		this.classList.add('hidden');
		//force a reflow
		this.offsetWidth;

		this.checkbox.checked = true;
		document.body.classList.add('overflow-hidden', 'has-overlay');
		this.backdrop.classList.add('opacity-0');
		this.content.classList.add(this.transitionContentClass);

		this.classList.remove('hidden');
		//force a reflow
		this.offsetWidth;
		trapFocus(this.querySelector('[tabindex="-1"]'), this.querySelector('input:not([type="hidden"])'));
		this.content.classList.remove(this.transitionContentClass);
		this.backdrop.classList.remove('opacity-0');

		// Check for overflow after content is loaded and adjust alignment
		if (this.flexMode) {
			this.checkAndAdjustOverflow();
		}
	}

	close(focusToggle = true, after = null) {
		const onTransitionEnd = () => {
			//remove this event listener
			this.backdrop.removeEventListener('transitionend', onTransitionEnd);

			this.backdrop.classList.remove('opacity-0');
			this.content.classList.remove(this.transitionContentClass);
			this.classList.add('hidden');
			this.classList.remove('block');
			document.body.classList.remove('overflow-hidden', 'has-overlay');
			this.dispatchEvent(new CustomEvent('close'));
			if (typeof after === 'function') {
				after();
			}
		};

		this.classList.remove('hidden');
		//force a reflow
		this.offsetWidth;

		removeTrapFocus(focusToggle ? this : null);
		this.checkbox.checked = false;

		this.backdrop.addEventListener('transitionend', onTransitionEnd);

		this.backdrop.classList.add('opacity-0');
		this.content.classList.add(this.transitionContentClass);
	}

	cleanup() {
		// Remove resize listener if it exists
		if (this.resizeHandler) {
			window.removeEventListener('resize', this.resizeHandler);
			this.resizeHandler = null;
		}
	}

	checkAndAdjustOverflow() {
		// Use requestAnimationFrame to ensure content is fully rendered
		requestAnimationFrame(() => {
			if (!this.content) return;

			const modalHeight = this.offsetHeight;
			const contentHeight = this.content.offsetHeight;
			const viewportHeight = window.innerHeight;

			// Check if content overflows the viewport
			if (contentHeight > viewportHeight) {
				// Switch to top alignment for scrollable content
				this.classList.remove('items-center');
				this.classList.add('items-start');
			} else {
				// Restore center alignment for content that fits
				this.classList.remove('items-start');
				this.classList.add('items-center');
			}
		});
	}

	disconnectedCallback() {
		this.cleanup();
	}
}

customElements.define('popup-modal', PopupModal);

if (!customElements.get('addon-recommendations')) {
	class AddonRecommendations extends HTMLElement {
		constructor() {
			super();
			this.contentContainer = this.querySelector('.modal-content');
			this.loadingContainer = this.querySelector('.loading-container');
			this.modal = this.closest('popup-modal');
			this.addAllButton = this.querySelector('button[data-add-all]');
			this.hideErrors = this.dataset.hideErrors === 'true';
			this.errorMessageWrapper = null;
			this.addAllButton = null;
			this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
		}

		closeModal() {
			if (this.modal && this.modal.isOpen()) {
				this.modal.close();
			}
		}

		handleErrorMessage(errorMessage = false) {
			if (this.hideErrors) return;

			this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.error-message-wrapper');
			if (!this.errorMessageWrapper) return;
			if (errorMessage) {
				this.errorMessageWrapper.innerHTML = errorMessage;
				this.errorMessageWrapper.classList.remove('hidden');
			} else {
				this.errorMessageWrapper.innerHTML = '';
				this.errorMessageWrapper.classList.add('hidden');
			}
		}

		addItemsToCart() {
			this.addAllButton.disabled = true;
			this.addAllButton.classList.add('loading');
			this.handleErrorMessage('');

			const addonItems = this.querySelectorAll('add-on-item');
			let formData = {
				items: [],
			};
			addonItems.forEach((item) => {
				formData.items.push({
					id: item.dataset.variantId,
					quantity: parseInt(item.querySelector('quantity-input input').value),
				});
			});

			console.log('send formData', formData);

			fetch(window.Shopify.routes.root + 'cart/add.js', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(formData),
			})
				.then((response) => response.json())
				.then((response) => {
					console.log('add to cart response', response);
					if (response.status) {
						publish(PUB_SUB_EVENTS.cartError, {
							source: 'addon-recommendations',
							productVariantId: formData.items[0].id,
							errors: response.errors || response.description,
							message: response.message,
						});
						this.handleErrorMessage(response.description);
						return;

						/*const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
						if (!soldOutMessage) return;
						this.submitButton.setAttribute('aria-disabled', true);
						this.submitButtonText.classList.add('hidden');
						soldOutMessage.classList.remove('hidden');
						this.error = true;
						return;*/
					} else if (!this.cart) {
						window.location = window.routes.cart_url;
					}

					if (!this.error) {
						publish(PUB_SUB_EVENTS.cartUpdate, {
							source: 'addon-recommendations',
							productVariantId: formData.items[0].id,
							cartData: response,
							openDrawer: true,
						}).then(() => {
							//CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);

							this.closeModal();
						});

						this.error = false;
					}
				})
				.catch((error) => {
					console.error('Error:', error);
				})
				.finally(() => {
					this.addAllButton.disabled = false;
					this.addAllButton.classList.remove('loading');
				});
		}

		checkQuantityInputs() {
			const quantityInputs = this.querySelectorAll('quantity-input input');
			let currentlySelected = 0;
			quantityInputs.forEach((input) => {
				if (input.value > 0) {
					currentlySelected++;
				}
			});

			if (currentlySelected == 0) {
				this.addAllButton.querySelector('.select-items').classList.remove('hidden');
				this.addAllButton.querySelector('.add-items-singular').classList.add('hidden');
				this.addAllButton.querySelector('.add-items-plural').classList.add('hidden');
			} else if (currentlySelected > 1) {
				this.addAllButton.querySelector('.select-items').classList.add('hidden');
				this.addAllButton.querySelector('.add-items-singular').classList.add('hidden');
				this.addAllButton.querySelector('.add-items-plural').classList.remove('hidden');
			} else {
				this.addAllButton.querySelector('.select-items').classList.add('hidden');
				this.addAllButton.querySelector('.add-items-singular').classList.remove('hidden');
				this.addAllButton.querySelector('.add-items-plural').classList.add('hidden');
			}
			this.addAllButton.disabled = currentlySelected == 0;
		}

		setupUI() {
			const closeModalButton = this.querySelector('button[data-close-modal]');
			const addonItems = this.querySelectorAll('add-on-item');

			this.errorMessageWrapper = this.querySelector('.error-message-wrapper');
			this.addAllButton = this.querySelector('button[data-add-all]');

			addonItems.forEach((item) => {
				item.addEventListener('change', () => {
					this.checkQuantityInputs();
				});
			});

			if (this.modal) {
				this.modal.addEventListener('close', () => {
					if (this.contentContainer) {
						this.contentContainer.innerHTML = '';
					}
				});
			}

			if (closeModalButton && this.modal) {
				closeModalButton.addEventListener('click', (event) => {
					event.preventDefault();
					this.closeModal();
				});
			}

			this.checkQuantityInputs();

			this.addAllButton.addEventListener('click', (event) => {
				event.preventDefault();
				this.addItemsToCart();
			});
		}

		loadRecommendations(productUrl, variantId) {
			this.loadingContainer.classList.remove('hidden');
			this.loadingContainer.classList.add('flex');
			fetch(`${productUrl}?variant_id=${variantId}&section_id=addon-recommendations`)
				.then((response) => response.text())
				.then((text) => {
					const html = document.createElement('div');
					html.innerHTML = text;
					const recommendations = html.querySelector('.shopify-section');
					if (this.contentContainer && recommendations) {
						this.contentContainer.innerHTML = recommendations.innerHTML;
					}

					this.setupUI();
				})
				.finally(() => {
					this.loadingContainer.classList.remove('flex');
					this.loadingContainer.classList.add('hidden');
				});
		}
	}

	customElements.define('addon-recommendations', AddonRecommendations);
}

if (!customElements.get('add-on-item')) {
	class AddOnItem extends HTMLElement {
		constructor() {
			super();

			this.selected = this.dataset.selected || false;
			this.quantityInput = this.querySelector('quantity-input input');
			this.variantSelect = this.querySelector('.variant-select');

			if (this.selected) {
				this.toggleSelection(true);
			} else {
				this.toggleSelection(false);
			}
		}

		connectedCallback() {
			this.addEventListener('click', (event) => {
				event.preventDefault();
				const selectParent = event.target.closest('select');
				const qtyParent = event.target.closest('quantity-input');
				if (
					selectParent ||
					qtyParent ||
					event.target.tagName.toLowerCase() === 'quantity-input' ||
					event.target.tagName.toLowerCase() === 'select'
				)
					return;
				this.toggleSelection();
			});

			if (this.quantityInput) {
				this.quantityInput.addEventListener('change', (event) => {
					if (this.selected && this.quantityInput && this.quantityInput.value == 0) {
						this.toggleSelection(false);
					} else if (!this.selected && (!this.quantityInput || this.quantityInput.value > 0)) {
						this.toggleSelection(true);
					}
				});
			}

			if (this.variantSelect) {
				this.variantSelect.addEventListener('change', (event) => {
					this.dataset.variantId = this.variantSelect.value;
					this.dispatchEvent(new CustomEvent('change'));
				});
			}
		}

		toggleSelection(force = null) {
			if (force === false) {
				this.classList.remove('selected');
			} else if (force === true) {
				this.classList.add('selected');
			} else {
				this.classList.toggle('selected');
			}

			this.selected = this.classList.contains('selected');

			if (this.selected && this.quantityInput && this.quantityInput.value == 0) {
				this.quantityInput.value = 1;
			} else if (!this.selected && (!this.quantityInput || this.quantityInput.value > 0)) {
				this.quantityInput.value = 0;
			}

			this.dispatchEvent(new CustomEvent('change'));
		}
	}
	customElements.define('add-on-item', AddOnItem);
}

window.root = document.documentElement;

const init = function () {
	const toggleTriggers = document.querySelectorAll('[data-toggle_class_target]');
	toggleTriggers.forEach((el) => {
		el.addEventListener('click', function (e) {
			const breakpoint = el.dataset['toggle_class_bp'] ? parseInt(el.dataset['toggle_class_bp'], 10) : null;

			if (breakpoint && window.innerWidth >= breakpoint) {
				return true;
			}
			e.preventDefault();
			const toggleClass = el.dataset['toggle_class'] ? el.dataset['toggle_class'] : 'hidden';
			const targets = document.querySelectorAll(el.dataset['toggle_class_target']);
			targets.forEach((tgt) => {
				tgt.classList.toggle(toggleClass);
			});
		});
	});

	const checklists = document.querySelectorAll('ul.interactive-checklist, ol.interactive-checklist');

	checklists.forEach((list, listIndex) => {
		const items = list.querySelectorAll('li');
		list.classList.add('text-lg', 'not-rte', 'list-none', 'ps-0', 'loaded');
		items.forEach((item, itemIndex) => {
			let checkbox = document.createElement('input'),
				label = document.createElement('label');
			const checkboxText = item.innerHTML;
			checkbox.setAttribute('type', 'checkbox');
			checkbox.setAttribute('id', 'checklist-item-' + listIndex + '-' + itemIndex);
			checkbox.setAttribute(
				'class',
				'appearance-none me-2 cursor-pointer w-6 h-6 rounded-sm border border-dark-300 text-primary-900 focus:ring-1 focus:ring-offset-1 focus:ring-primary-900 checked:bg-primary-900 disabled:opacity-50'
			);
			label.innerHTML = checkboxText;
			label.setAttribute('for', 'checklist-item-' + listIndex + '-' + itemIndex);
			label.setAttribute('class', 'cursor-pointer font-semibold');
			item.innerHTML = '';
			item.classList.add('flex', 'items-center', 'mb-3');
			item.appendChild(checkbox);
			item.appendChild(label);
		});
	});
};
window.addEventListener('DOMContentLoaded', init);


let wHSaved = Math.min(window.innerHeight, window.outerHeight);

// get the zoom scale for pinch-to-zoom
let viewportScale = 1;
window.visualViewport.addEventListener('resize', (e) => {
	viewportScale = e.target.scale;
});

const updateCssVars = function (e) {
	// don't update if pinch-to-zoom
	if (viewportScale > 1) return;
	const wH = Math.min(window.innerHeight, window.outerHeight);
	const siteHeaderNav = document.getElementById('shopify-section-navbar');
	const announcementBar = document.getElementById('shopify-section-announcement-bar');
	if (wH == 0) return;
	let navHeight = 0;

	if (siteHeaderNav) {
		navHeight += siteHeaderNav.offsetHeight;
	}

	if (!wHSaved || Math.abs(wHSaved - wH) > 150) {
		wHSaved = wH;
	}
	// set the min-vh-100 height to avoid issues with status bar...
	root.style.setProperty('--vh', `${wHSaved / 100}px`);

	// set --nav-height css var
	root.style.setProperty('--nav-height', `${navHeight}px`);

	const productGallery = document.getElementById('product-gallery');
	if (productGallery) {
		const pgTop = Math.round(productGallery.getBoundingClientRect().top) + window.scrollY;
		root.style.setProperty('--product-gallery-top', `${pgTop}px`);
	}

	const collectionFilters = document.getElementById('collection-filters');
	if (collectionFilters) {
		const cfTop = Math.round(collectionFilters.getBoundingClientRect().top);
		root.style.setProperty('--collection-filters-top', `${cfTop}px`);
	}
};

if (document.getElementById('site-header-nav')) {
	updateCssVars();

	'resize orientationchange load pageshow'.split(' ').forEach((eventName) => {
		window.addEventListener(eventName, updateCssVars);
	});

	window.addEventListener('focus', () => {
		setTimeout(updateCssVars, 10);
	});
	//document.addEventListener("visibilitychange", updateCssVars);
}

const collectionFilters = document.getElementById('collection-filters');
if (collectionFilters) {
	const cfTop = Math.round(collectionFilters.getBoundingClientRect().top) + window.scrollY;
	root.style.setProperty('--collection-filters-top', `${cfTop}px`);
}